import { z } from 'zod';
export var FlowFilterOperation;
(function (FlowFilterOperation) {
    FlowFilterOperation["Equals"] = "Equals";
    FlowFilterOperation["NotEquals"] = "NotEquals";
    FlowFilterOperation["GreaterThan"] = "GreaterThan";
    FlowFilterOperation["GreaterThanOrEqualTo"] = "GreaterThanOrEqualTo";
    FlowFilterOperation["LessThan"] = "LessThan";
    FlowFilterOperation["LessThanOrEqualTo"] = "LessThanOrEqualTo";
    FlowFilterOperation["Contains"] = "Contains";
    FlowFilterOperation["DoesNotContain"] = "DoesNotContain";
    FlowFilterOperation["StartsWith"] = "StartsWith";
    FlowFilterOperation["EndsWith"] = "EndsWith";
    FlowFilterOperation["IsOneOf"] = "IsOneOf";
    FlowFilterOperation["IsNotOneOf"] = "IsNotOneOf";
    FlowFilterOperation["IsNull"] = "IsNull";
    FlowFilterOperation["IsNotNull"] = "IsNotNull";
})(FlowFilterOperation || (FlowFilterOperation = {}));
export var FlowFieldDataType;
(function (FlowFieldDataType) {
    FlowFieldDataType["String"] = "String";
    FlowFieldDataType["Date"] = "Date";
    FlowFieldDataType["DateTime"] = "DateTime";
    FlowFieldDataType["Number"] = "Number";
    FlowFieldDataType["Boolean"] = "Boolean";
    FlowFieldDataType["List"] = "List";
})(FlowFieldDataType || (FlowFieldDataType = {}));
export var FlowFilterMatchRule;
(function (FlowFilterMatchRule) {
    FlowFilterMatchRule["All"] = "All";
    FlowFilterMatchRule["Any"] = "Any";
})(FlowFilterMatchRule || (FlowFilterMatchRule = {}));
export var FlowCaseSensitivity;
(function (FlowCaseSensitivity) {
    FlowCaseSensitivity["ExactMatch"] = "ExactMatch";
    FlowCaseSensitivity["IgnoreCase"] = "IgnoreCase";
})(FlowCaseSensitivity || (FlowCaseSensitivity = {}));
export var FlowSortDirection;
(function (FlowSortDirection) {
    FlowSortDirection["Ascending"] = "Ascending";
    FlowSortDirection["Descending"] = "Descending";
})(FlowSortDirection || (FlowSortDirection = {}));
export var FlowLimitReturnType;
(function (FlowLimitReturnType) {
    FlowLimitReturnType["List"] = "List";
    FlowLimitReturnType["LimitedList"] = "LimitedList";
    FlowLimitReturnType["FirstObject"] = "FirstObject";
})(FlowLimitReturnType || (FlowLimitReturnType = {}));
export const FlowFieldDataTypesWithoutList = Object.values(FlowFieldDataType).filter((type) => type !== FlowFieldDataType.List);
const ComparableFieldDataTypes = [
    FlowFieldDataType.Date,
    FlowFieldDataType.DateTime,
    FlowFieldDataType.Number,
];
export const FlowValuelessFilterOperations = [
    FlowFilterOperation.IsNull,
    FlowFilterOperation.IsNotNull,
];
const ValueFilterOperations = Object.values(FlowFilterOperation).filter((operation) => !FlowValuelessFilterOperations.includes(operation));
export const FlowMultiValueFilterOperations = [
    FlowFilterOperation.IsOneOf,
    FlowFilterOperation.IsNotOneOf,
];
export const FLOW_FILTER_OP_SUPPORTED_TYPES = {
    [FlowFilterOperation.Equals]: FlowFieldDataTypesWithoutList,
    [FlowFilterOperation.NotEquals]: FlowFieldDataTypesWithoutList,
    [FlowFilterOperation.GreaterThan]: ComparableFieldDataTypes,
    [FlowFilterOperation.GreaterThanOrEqualTo]: ComparableFieldDataTypes,
    [FlowFilterOperation.LessThan]: ComparableFieldDataTypes,
    [FlowFilterOperation.LessThanOrEqualTo]: ComparableFieldDataTypes,
    [FlowFilterOperation.Contains]: [FlowFieldDataType.String, FlowFieldDataType.List],
    [FlowFilterOperation.DoesNotContain]: [FlowFieldDataType.String, FlowFieldDataType.List],
    [FlowFilterOperation.IsOneOf]: [
        FlowFieldDataType.String,
        FlowFieldDataType.List,
        FlowFieldDataType.Number,
    ],
    [FlowFilterOperation.IsNotOneOf]: [
        FlowFieldDataType.String,
        FlowFieldDataType.List,
        FlowFieldDataType.Number,
    ],
    [FlowFilterOperation.StartsWith]: [FlowFieldDataType.String],
    [FlowFilterOperation.EndsWith]: [FlowFieldDataType.String],
    [FlowFilterOperation.IsNull]: FlowFieldDataTypesWithoutList,
    [FlowFilterOperation.IsNotNull]: FlowFieldDataTypesWithoutList,
};
export const FLOW_FILTER_TYPE_SUPPORTED_OPS = Object.fromEntries(Object.values(FlowFieldDataType).map((dataType) => [
    dataType,
    new Set(Object.entries(FLOW_FILTER_OP_SUPPORTED_TYPES)
        .filter(([, types]) => types.includes(dataType))
        .map(([operation]) => operation)),
]));
const BaseFilterConditionSchema = z.object({
    id: z.uuid(),
    fieldToFilter: z.string().min(1),
    dataType: z.enum(FlowFieldDataType),
});
const ValuelessFilterConditionSchema = BaseFilterConditionSchema.extend({
    operation: z.enum(FlowValuelessFilterOperations),
}).strip();
export const FlowCaseSensitivityDataTypes = [
    FlowFieldDataType.String,
    FlowFieldDataType.List,
];
const ValueFilterConditionSchema = BaseFilterConditionSchema.extend({
    caseSensitivity: z.enum(FlowCaseSensitivity).default(FlowCaseSensitivity.ExactMatch).optional(),
    operation: z.enum(ValueFilterOperations),
    value: z.string().min(1),
});
export const FlowFilterConditionSchema = z
    .discriminatedUnion('operation', [ValuelessFilterConditionSchema, ValueFilterConditionSchema])
    .refine((condition) => FLOW_FILTER_OP_SUPPORTED_TYPES[condition.operation].includes(condition.dataType), {
    path: ['operation'],
    message: `Operation not supported for data type`,
});
export const FlowFilterConfigSchema = z.object({
    filterMatchRule: z.enum(FlowFilterMatchRule),
    filterConditions: z.array(FlowFilterConditionSchema),
});
export const FlowSortConfigSchema = z.object({
    fieldToSort: z.string(),
    dataType: z.enum(FlowFieldDataType),
    sortDirection: z.enum(FlowSortDirection).default(FlowSortDirection.Ascending),
});
export const FlowLimitConfigSchema = z.object({
    limit: z
        .union([z.string(), z.number(), z.null(), z.undefined()])
        .transform((value) => value === '' || value === null || value === undefined ? undefined : Number(value))
        .pipe(z.number().min(1).optional())
        .optional(),
    offset: z
        .union([z.string(), z.number(), z.null(), z.undefined()])
        .transform((value) => value === '' || value === null || value === undefined ? undefined : Number(value))
        .pipe(z.number().min(0).optional().default(0))
        .default(0),
    returnType: z.enum(FlowLimitReturnType).default(FlowLimitReturnType.List),
});
//# sourceMappingURL=filterConfig.js.map